import { useState, useMemo, useEffect } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { HospitalDto } from "@ambulance/shared";
import { useApi } from "../api";
import { useMe } from "../hooks/useMe";
import {
  Loader2,
  Search,
  AlertCircle,
  ShieldCheck,
  Download,
  X,
} from "lucide-react";
import { apiUrl } from "../auth";

type Row = {
  _id: string;
  id: string;
  hospitalId: string;
  hospitalName: string;
  ambulanceNo: string;
  date: string;
  shift: string;
  level: string;
  type: string;
  submittedAt: number;
  submittedByName: string | null;
  outgoingMedicName: string;
  incomingMedicName: string;
};

// Rows added per "Show 50 more" click.
const PAGE_STEP = 50;

export function ChecklistsOverviewPage() {
  const { me } = useMe();
  const api = useApi();
  const hospitalsQ = useQuery({ queryKey: ["hospitals"], queryFn: () => api.get<HospitalDto[]>("/api/v1/hospitals"), enabled: Boolean(api.token) });
  const hospitals = hospitalsQ.data?.map((h) => ({ ...h, _id: h.id }));
  // Bootstrapping the first admin is handled by tenant provisioning now.
  const promote = async () => false;
  const [hospitalFilter, setHospitalFilter] = useState<string>("");
  const [search, setSearch] = useState("");
  // Date range filter — defaults to the last 2 days (yesterday + today).
  const localDate = (d: Date) => {
    const p = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  };
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return localDate(d);
  });
  const [endDate, setEndDate] = useState(() => localDate(new Date()));
  const [showExport, setShowExport] = useState(false);
  // Cursor-paginated: 50 rows up front, "Show 50 more" walks the rest.
  // Changing any filter above resets the hook to a fresh first page.
  const listQ = useInfiniteQuery({
    queryKey: ["checklists", hospitalFilter, startDate, endDate, api.token],
    queryFn: ({ pageParam }) => {
      const qs = new URLSearchParams();
      if (hospitalFilter) qs.set("hospitalId", hospitalFilter);
      if (startDate) qs.set("startDate", startDate);
      if (endDate) qs.set("endDate", endDate);
      if (pageParam) qs.set("cursor", pageParam);
      qs.set("limit", String(PAGE_STEP));
      return api.get<{ page: Omit<Row, "_id">[]; nextCursor: string | null }>(`/api/v1/checklists?${qs}`);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: Boolean(api.token),
  });
  const rows: Row[] = useMemo(() => (listQ.data?.pages ?? []).flatMap((p) => p.page.map((r) => ({ ...r, _id: r.id }))), [listQ.data]);
  const listStatus = listQ.isPending ? "LoadingFirstPage" : listQ.isFetchingNextPage ? "LoadingMore" : listQ.hasNextPage ? "CanLoadMore" : "Exhausted";
  const loadMore = (_n: number) => {
    void listQ.fetchNextPage();
  };

  // How many rows the user has asked to see; grows by PAGE_STEP per click.
  // A supervisor's "All my hospitals" view is thinned server-side after
  // pagination, so a raw page can come back with only a couple of visible
  // rows — the effect below keeps loading until the target is met or the
  // range runs out, so one click always yields a full step (never "2 rows
  // per click").
  const [targetCount, setTargetCount] = useState(PAGE_STEP);
  // New filters start a fresh list, so the target resets with them
  // (render-time state adjustment, per React's "adjusting state when props
  // change" pattern).
  const filterKey = `${hospitalFilter}|${startDate}|${endDate}`;
  const [prevFilterKey, setPrevFilterKey] = useState(filterKey);
  if (prevFilterKey !== filterKey) {
    setPrevFilterKey(filterKey);
    setTargetCount(PAGE_STEP);
  }
  useEffect(() => {
    if (listStatus === "CanLoadMore" && rows.length < targetCount) {
      loadMore(PAGE_STEP);
    }
  }, [listStatus, rows.length, targetCount, loadMore]);

  const loadingMore =
    listStatus === "LoadingMore" ||
    (listStatus === "CanLoadMore" && rows.length < targetCount);

  const filtered = useMemo(() => {
    if (!rows) return [];
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter(
      (c) =>
        c.ambulanceNo.toLowerCase().includes(q) ||
        c.hospitalName.toLowerCase().includes(q) ||
        c.outgoingMedicName?.toLowerCase().includes(q) ||
        c.incomingMedicName?.toLowerCase().includes(q)
    );
  }, [rows, search]);

  const showBootstrap = false as boolean;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Checklists Overview</h1>
        <div className="flex flex-wrap items-center gap-2">
          {me?.role === "super_admin" && (
            <>
              <button
                onClick={() => setShowExport(true)}
                className="rounded-lg bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold px-4 py-2 inline-flex items-center gap-2"
              >
                <Download className="w-4 h-4" /> Export CSV
              </button>
            </>
          )}
          <Link
            to="/checklist"
            className="rounded-lg bg-[var(--hmg-red)] hover:bg-[var(--hmg-red-dark)] text-white text-sm font-semibold px-4 py-2"
          >
            + New Checklist
          </Link>
        </div>
      </div>

      {showBootstrap && (
        <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 p-4 flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-amber-900 font-medium">
              No super admin set up yet
            </p>
            <p className="text-xs text-amber-800 mt-1">
              Click below to claim Super Admin role for this account (only works
              if no admin exists).
            </p>
            <button
              onClick={async () => {
                const result = await promote();
                if (result) window.location.reload();
                else alert("A super admin already exists.");
              }}
              className="mt-2 text-sm font-semibold text-amber-900 underline"
            >
              Become Super Admin
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search by ambulance, hospital, medic..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[var(--hmg-red)]"
          />
        </div>
        <select
          value={hospitalFilter}
          onChange={(e) => setHospitalFilter(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[var(--hmg-red)]"
        >
          <option value="">All my hospitals</option>
          {hospitals?.map((h) => (
            <option key={h._id} value={h._id}>
              {h.name}
            </option>
          ))}
        </select>
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-500">From</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="px-2 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[var(--hmg-red)]"
          />
          <label className="text-xs text-slate-500">To</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="px-2 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[var(--hmg-red)]"
          />
          {(startDate || endDate) && (
            <button
              type="button"
              onClick={() => {
                setStartDate("");
                setEndDate("");
              }}
              className="text-xs font-semibold text-slate-500 hover:text-[var(--hmg-red)]"
              title="Show all dates"
            >
              All dates
            </button>
          )}
        </div>
      </div>

      {listStatus === "LoadingFirstPage" ? (
        <div className="py-12 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-[var(--hmg-red)]" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <AlertCircle className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600">
            {rows.length === 0
              ? "No checklists submitted yet."
              : "No results match your filters."}
          </p>
        </div>
      ) : (
        <>
          <div className="hidden md:block bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-600">
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Hospital</th>
                    <th className="px-4 py-3">Ambulance #</th>
                    <th className="px-4 py-3">Shift / Level</th>
                    <th className="px-4 py-3">Outgoing → Incoming</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((c) => (
                    <tr key={c._id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        <div className="font-medium text-slate-900">{c.date}</div>
                        <div className="text-xs text-slate-500">
                          {new Date(c.submittedAt).toLocaleString(undefined, { hour12: false })}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">
                        {c.hospitalName}
                      </td>
                      <td className="px-4 py-3 text-sm font-mono">
                        {c.ambulanceNo}
                      </td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        <span
                          className={`inline-block text-xs px-2 py-0.5 rounded ${c.shift === "D" ? "bg-amber-100 text-amber-800" : "bg-indigo-100 text-indigo-800"}`}
                        >
                          {c.shift === "D" ? "Day" : "Night"}
                        </span>
                        <span className="ml-1 inline-block text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                          {c.level}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">
                        {c.outgoingMedicName || "—"} →{" "}
                        {c.incomingMedicName || "—"}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <Link
                          to="/Checklists-Overview/$id"
                          params={{ id: c._id }}
                          className="text-sm font-semibold text-[var(--hmg-red)]"
                        >
                          View &rarr;
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="md:hidden space-y-3">
            {filtered.map((c) => (
              <Link
                key={c._id}
                to="/Checklists-Overview/$id"
                params={{ id: c._id }}
                className="block bg-white rounded-xl border border-slate-200 p-4 hover:bg-slate-50"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="font-semibold text-slate-900">{c.date}</div>
                  <div className="flex gap-1">
                    <span
                      className={`inline-block text-xs px-2 py-0.5 rounded ${c.shift === "D" ? "bg-amber-100 text-amber-800" : "bg-indigo-100 text-indigo-800"}`}
                    >
                      {c.shift === "D" ? "Day" : "Night"}
                    </span>
                    <span className="inline-block text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                      {c.level}
                    </span>
                  </div>
                </div>
                <div className="text-sm text-slate-700 mb-1">{c.hospitalName}</div>
                <div className="text-sm font-mono text-slate-900 mb-1">
                  Ambulance: {c.ambulanceNo}
                </div>
                <div className="text-xs text-slate-600">
                  {c.outgoingMedicName || "—"} → {c.incomingMedicName || "—"}
                </div>
              </Link>
            ))}
          </div>
        </>
      )}

      {/* Kept outside the list so it's still reachable when a search matches
          nothing in the loaded rows — loading more widens the search. */}
      {listStatus !== "LoadingFirstPage" && rows.length > 0 && (
        <div className="mt-4 flex flex-col items-center gap-1.5">
          {listStatus === "Exhausted" ? (
            <span className="text-xs text-slate-500">
              All {rows.length} checklist{rows.length === 1 ? "" : "s"} loaded
            </span>
          ) : (
            <>
              <button
                onClick={() => setTargetCount(rows.length + PAGE_STEP)}
                disabled={loadingMore}
                className="rounded-lg bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold px-6 py-2 inline-flex items-center gap-2 disabled:opacity-60"
              >
                {loadingMore && <Loader2 className="w-4 h-4 animate-spin" />}
                Show 50 more
              </button>
              <span className="text-xs text-slate-500">
                {rows.length} loaded
                {search ? " — search only looks at these" : ""}
              </span>
            </>
          )}
        </div>
      )}

      {showExport && (
        <ExportModal
          onClose={() => setShowExport(false)}
          runExport={async (start, end, onProgress) => {
            // The API builds the whole CSV server-side (every page, same columns as before).
            const qs = new URLSearchParams();
            if (start) qs.set("startDate", start);
            if (end) qs.set("endDate", end);
            const res = await fetch(`${apiUrl}/api/v1/checklists/export?${qs}`, { headers: { Authorization: `Bearer ${api.token}` } });
            if (!res.ok) throw new Error(`Export failed (${res.status})`);
            const csv = await res.text();
            const count = Math.max(0, csv.split("\r\n").length - 1);
            onProgress(count);
            if (count === 0) return 0;
            const stamp = start || end ? `_${start || "start"}_to_${end || "end"}` : "_all";
            const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `ambulance-checklists${stamp}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            return count;
          }}
        />
      )}
    </div>
  );
}

function ExportModal({
  onClose,
  runExport,
}: {
  onClose: () => void;
  runExport: (
    start: string | undefined,
    end: string | undefined,
    onProgress: (loaded: number) => void
  ) => Promise<number>;
}) {
  const [mode, setMode] = useState<"all" | "range">("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<number | null>(null);

  async function handleExport() {
    setError(null);
    if (mode === "range") {
      if (!startDate || !endDate) {
        setError("Please choose both a start and end date.");
        return;
      }
      if (startDate > endDate) {
        setError("Start date must be before end date.");
        return;
      }
    }
    setBusy(true);
    setLoaded(0);
    try {
      const count = await runExport(
        mode === "range" ? startDate : undefined,
        mode === "range" ? endDate : undefined,
        setLoaded
      );
      if (count === 0) {
        setError("No checklists found for the selected range.");
      } else {
        setDone(count);
      }
    } catch (e: any) {
      setError(e?.message ?? "Export failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold inline-flex items-center gap-2">
            <Download className="w-5 h-5" /> Export checklists to CSV
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        {done !== null ? (
          <div className="space-y-4">
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm p-3">
              Exported {done} checklist{done === 1 ? "" : "s"}. Your download
              should begin automatically.
            </div>
            <button
              onClick={onClose}
              className="w-full rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-2"
            >
              Close
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Each checklist becomes one row, with every checklist item as its
              own column.
            </p>

            <div className="space-y-2">
              <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-50">
                <input
                  type="radio"
                  name="exportMode"
                  checked={mode === "all"}
                  onChange={() => setMode("all")}
                  className="mt-0.5"
                />
                <span>
                  <span className="block text-sm font-medium text-slate-900">
                    All data
                  </span>
                  <span className="block text-xs text-slate-500">
                    Export every submitted checklist.
                  </span>
                </span>
              </label>

              <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-50">
                <input
                  type="radio"
                  name="exportMode"
                  checked={mode === "range"}
                  onChange={() => setMode("range")}
                  className="mt-0.5"
                />
                <span className="flex-1">
                  <span className="block text-sm font-medium text-slate-900">
                    Date range
                  </span>
                  <span className="block text-xs text-slate-500 mb-2">
                    Only checklists within the selected dates.
                  </span>
                  {mode === "range" && (
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">
                          From
                        </label>
                        <input
                          type="date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--hmg-red)]"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">
                          To
                        </label>
                        <input
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--hmg-red)]"
                        />
                      </div>
                    </div>
                  )}
                </span>
              </label>
            </div>

            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm p-3">
                {error}
              </div>
            )}

            <div className="flex gap-2 pt-1">
              <button
                onClick={handleExport}
                disabled={busy}
                className="flex-1 rounded-lg bg-[var(--hmg-red)] hover:bg-[var(--hmg-red-dark)] text-white font-semibold py-2 disabled:opacity-60 inline-flex items-center justify-center gap-2"
              >
                {busy ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                {/* A big export takes several round-trips, so show it moving
                    rather than leaving the button spinning silently. */}
                {busy && loaded > 0 ? `Collecting ${loaded}…` : "Export"}
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
